const PWA_UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;
const PWA_UPDATE_APPLY_TIMEOUT_MS = 30 * 1000;
const SKIP_WAITING_MESSAGE = { type: 'SKIP_WAITING' };
const PWA_UPDATE_ACTIVATED_MESSAGE_TYPE = 'PWA_UPDATE_ACTIVATED';

type PwaUpdateListener = () => void;

const pwaUpdateListeners = new Set<PwaUpdateListener>();
const watchedInstallingServiceWorkers = new WeakSet<ServiceWorker>();
let waitingServiceWorker: ServiceWorker | null = null;
let waitingWorkerCleanup: (() => void) | undefined;
let pwaRegistration: ServiceWorkerRegistration | null = null;
let isApplyingPwaUpdate = false;
let didActivateWaitingUpdateInAnotherTab = false;
let isReloadingForPwaUpdate = false;
let acceptedWaitingServiceWorker: ServiceWorker | null = null;
let acceptedWorkerCleanup: (() => void) | undefined;
let applyResetTimeout: number | undefined;

const reloadForPwaUpdate = () => {
  if (isReloadingForPwaUpdate) {
    return;
  }

  isReloadingForPwaUpdate = true;
  window.location.reload();
};

const resetPwaUpdateApplyState = (notifyListeners = false) => {
  acceptedWorkerCleanup?.();
  acceptedWorkerCleanup = undefined;
  acceptedWaitingServiceWorker = null;
  isApplyingPwaUpdate = false;

  if (applyResetTimeout !== undefined) {
    window.clearTimeout(applyResetTimeout);
    applyResetTimeout = undefined;
  }

  if (notifyListeners) {
    pwaUpdateListeners.forEach((listener) => listener());
  }
};

const notifyPwaUpdateListeners = () => {
  pwaUpdateListeners.forEach((listener) => listener());
};

const clearWaitingServiceWorker = (worker: ServiceWorker | null = waitingServiceWorker) => {
  if (worker && waitingServiceWorker !== worker) {
    return;
  }

  waitingWorkerCleanup?.();
  waitingWorkerCleanup = undefined;
  waitingServiceWorker = null;
};

const trackWaitingServiceWorker = (worker: ServiceWorker) => {
  waitingWorkerCleanup?.();

  const handleStateChange = () => {
    if (waitingServiceWorker !== worker) {
      return;
    }

    if (worker.state === 'activated') {
      if (acceptedWaitingServiceWorker === worker) {
        return;
      }

      clearWaitingServiceWorker(worker);
      resetPwaUpdateApplyState();
      didActivateWaitingUpdateInAnotherTab = true;
      notifyPwaUpdateListeners();
      return;
    }

    if (worker.state === 'redundant') {
      clearWaitingServiceWorker(worker);
      didActivateWaitingUpdateInAnotherTab = false;
      resetPwaUpdateApplyState(true);
    }
  };

  worker.addEventListener('statechange', handleStateChange);
  waitingWorkerCleanup = () => {
    worker.removeEventListener('statechange', handleStateChange);
  };
};

const notifyPwaUpdateAvailable = (worker: ServiceWorker) => {
  if (worker.state === 'activated') {
    clearWaitingServiceWorker(worker);
    didActivateWaitingUpdateInAnotherTab = true;
    notifyPwaUpdateListeners();
    return;
  }

  if (worker.state === 'redundant') {
    clearWaitingServiceWorker(worker);
    didActivateWaitingUpdateInAnotherTab = false;
    resetPwaUpdateApplyState(true);
    return;
  }

  if (waitingServiceWorker === worker) {
    return;
  }

  resetPwaUpdateApplyState();
  waitingServiceWorker = worker;
  trackWaitingServiceWorker(worker);
  didActivateWaitingUpdateInAnotherTab = false;
  notifyPwaUpdateListeners();
};

const notifyPwaUpdateActivatedInAnotherTab = () => {
  if (waitingServiceWorker) {
    didActivateWaitingUpdateInAnotherTab = false;
    return;
  }

  didActivateWaitingUpdateInAnotherTab = true;

  notifyPwaUpdateListeners();
};

export const hasPwaUpdateAvailable = (): boolean => Boolean(waitingServiceWorker || didActivateWaitingUpdateInAnotherTab);

export const subscribeToPwaUpdateAvailable = (listener: PwaUpdateListener): (() => void) => {
  pwaUpdateListeners.add(listener);

  if (hasPwaUpdateAvailable()) {
    queueMicrotask(listener);
  }

  return () => {
    pwaUpdateListeners.delete(listener);
  };
};

const trackAcceptedWaitingServiceWorker = (worker: ServiceWorker) => {
  resetPwaUpdateApplyState();
  acceptedWaitingServiceWorker = worker;
  isApplyingPwaUpdate = true;

  const handleStateChange = () => {
    if (acceptedWaitingServiceWorker !== worker) {
      return;
    }

    if (worker.state === 'activated') {
      reloadForPwaUpdate();
      return;
    }

    if (worker.state === 'redundant') {
      clearWaitingServiceWorker(worker);
      resetPwaUpdateApplyState(true);
    }
  };

  worker.addEventListener('statechange', handleStateChange);
  acceptedWorkerCleanup = () => {
    worker.removeEventListener('statechange', handleStateChange);
  };
  applyResetTimeout = window.setTimeout(() => {
    if (acceptedWaitingServiceWorker === worker) {
      if (worker.state === 'redundant') {
        clearWaitingServiceWorker(worker);
      }

      resetPwaUpdateApplyState(true);
    }
  }, PWA_UPDATE_APPLY_TIMEOUT_MS);
};

const refreshWaitingServiceWorker = (): ServiceWorker | null => {
  if (waitingServiceWorker?.state === 'activated') {
    clearWaitingServiceWorker();
    didActivateWaitingUpdateInAnotherTab = true;
    return null;
  }

  if (waitingServiceWorker?.state === 'redundant') {
    clearWaitingServiceWorker();
    resetPwaUpdateApplyState();
  }

  const currentWaitingWorker = pwaRegistration?.waiting ?? null;

  if (
    currentWaitingWorker &&
    currentWaitingWorker.state !== 'activated' &&
    currentWaitingWorker.state !== 'redundant' &&
    navigator.serviceWorker.controller &&
    waitingServiceWorker !== currentWaitingWorker
  ) {
    didActivateWaitingUpdateInAnotherTab = false;
    notifyPwaUpdateAvailable(currentWaitingWorker);
  }

  if (waitingServiceWorker) {
    didActivateWaitingUpdateInAnotherTab = false;
  }

  return waitingServiceWorker;
};

export const applyPwaUpdate = (): boolean => {
  const currentWaitingWorker = refreshWaitingServiceWorker();

  if (!currentWaitingWorker) {
    if (didActivateWaitingUpdateInAnotherTab) {
      isApplyingPwaUpdate = true;
      reloadForPwaUpdate();
      return true;
    }

    return false;
  }

  if (acceptedWaitingServiceWorker === currentWaitingWorker) {
    return true;
  }

  trackAcceptedWaitingServiceWorker(currentWaitingWorker);

  try {
    currentWaitingWorker.postMessage(SKIP_WAITING_MESSAGE);
  } catch (error) {
    console.debug('Service worker update apply skipped:', error);
    resetPwaUpdateApplyState(true);
    return false;
  }

  return true;
};

const checkForServiceWorkerUpdate = (registration: ServiceWorkerRegistration) => {
  registration
    .update()
    .then(() => {
      if (registration.waiting && navigator.serviceWorker.controller) {
        notifyPwaUpdateAvailable(registration.waiting);
        return;
      }

      watchInstallingServiceWorker(registration);
    })
    .catch((error) => {
      console.debug('Service worker update check skipped:', error);
    });
};

const watchInstallingServiceWorker = (registration: ServiceWorkerRegistration) => {
  const installingWorker = registration.installing;

  if (!installingWorker) {
    return;
  }

  if (installingWorker.state === 'installed' && navigator.serviceWorker.controller) {
    notifyPwaUpdateAvailable(installingWorker);
    return;
  }

  if (watchedInstallingServiceWorkers.has(installingWorker)) {
    return;
  }

  watchedInstallingServiceWorkers.add(installingWorker);
  installingWorker.addEventListener('statechange', () => {
    if (installingWorker.state === 'installed' && navigator.serviceWorker.controller) {
      notifyPwaUpdateAvailable(installingWorker);
    }
  });
};

const watchForServiceWorkerUpdates = (registration: ServiceWorkerRegistration) => {
  if (registration.waiting && navigator.serviceWorker.controller) {
    notifyPwaUpdateAvailable(registration.waiting);
  }

  watchInstallingServiceWorker(registration);

  registration.addEventListener('updatefound', () => {
    watchInstallingServiceWorker(registration);
  });

  checkForServiceWorkerUpdate(registration);

  window.setInterval(() => {
    checkForServiceWorkerUpdate(registration);
  }, PWA_UPDATE_CHECK_INTERVAL_MS);

  window.addEventListener('focus', () => {
    checkForServiceWorkerUpdate(registration);
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkForServiceWorkerUpdate(registration);
    }
  });
};

/**
 * Registers the service worker, checks for app updates during long-lived
 * sessions, and reloads only after the user accepts a waiting update.
 */
export function registerPwaUpdateReload(): void {
  if (import.meta.env.MODE === 'development' || !('serviceWorker' in navigator)) {
    return;
  }

  let hasControllingServiceWorker = Boolean(navigator.serviceWorker.controller);
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === PWA_UPDATE_ACTIVATED_MESSAGE_TYPE && !isApplyingPwaUpdate) {
      notifyPwaUpdateActivatedInAnotherTab();
    }
  });

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hasControllingServiceWorker) {
      hasControllingServiceWorker = true;
      return;
    }

    if (!isApplyingPwaUpdate) {
      notifyPwaUpdateActivatedInAnotherTab();
      return;
    }

    reloadForPwaUpdate();
  });

  navigator.serviceWorker
    .register('/sw.js', { scope: '/' })
    .then((registration) => {
      pwaRegistration = registration;
      watchForServiceWorkerUpdates(registration);
    })
    .catch((error) => {
      console.error('Failed to register service worker:', error);
    });
}
